import { Grid, Typography } from '@mui/material';
import { IRoom } from '../../../interfaces/IPartnerPms';
import { useCallback, useEffect, useMemo } from 'react';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import { ActionButton } from '../../Button';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import FormHelper from '../../../helpers/FormHelper';
import PartnerPmsService from '../../../services/PartnerPmsService';
import SelectField from '../../SelectField';
import { useAuthContext } from '../../../contexts/AuthContext';
import DateField from '../../DateField';
import TextField from '../../TextField';
import ValidationHelper from '../../../helpers/ValidationHelper';
import DefaultConstants from '../../../data/Constants';
import DateTimeHelper from '../../../helpers/DateTimeHelper';
import useState from '../../../hooks/useState';
import useDialog from '../../../hooks/useDialog';


interface IAddRoomBlockingDialogProps extends IDefaultDialogProps {
    room: IRoom;
    handleRefreshSelectedRoom: () => void;
}

interface IStateValues {
    startDate: Date | undefined;
    endDate: Date | undefined;
    typeId: number | undefined;
    description: string;
}

const AddRoomBlockingDialog: React.FC<IAddRoomBlockingDialogProps> = ({
    open,
    handleClose,
    room,
    handleRefreshSelectedRoom
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();

    const state = useState<IStateValues>({
        startDate: undefined,
        endDate: undefined,
        typeId: undefined,
        description: ''
    });


    const confirmationDialog = useDialog();


    useEffect(() => {
        handleResetFields();
        // eslint-disable-next-line
    }, []);


    const handleResetFields = useCallback(() => {
        const now = DateTimeHelper.ToDate(undefined);

        state.reset();
        state.update('startDate', now);
        state.update('endDate', now);

        confirmationDialog.handleClose();
        // eslint-disable-next-line
    }, []);
    

    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (room === undefined) {
            return;
        }

        let formData = FormHelper.Create(event);
        formData = FormHelper.Add(formData, 'roomId', room.id);

        PartnerPmsService.AddRoomBlocking(formData).then(() => {
            handleClose();
            handleRefreshSelectedRoom();
            handleResetFields();
        });

        // eslint-disable-next-line
    }, [room]);


    const dialogActions = useMemo(() => {
        const response = [<ActionButton action='close' onClick={handleClose} />];

        const { typeId, startDate, endDate, description } = state.values;
        
        if (typeId && startDate && endDate && description && description.length > 0) {
            response.push(<ActionButton action='save' onClick={confirmationDialog.handleOpen} />);
        } else {
            response.push(<ActionButton action='save' type='submit' />);
        }

        return response;
        // eslint-disable-next-line
    }, [translate, state.values]);
    

    return (
        <>
            <Dialog
                open={open}
                handleClose={handleClose}
                title={translate('blockRoom')}
                onSubmit={handleSubmit}
                dialogActions={dialogActions}
                className='dialogactions-spacebetween overflow-inherit'
                absolute={true}
                dividers={true}
            >
                <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                    <Grid item md={6} xs={12}>
                        <DateField
                            label={translate('startDate')}
                            format='DD/MM/YYYY'
                            name='startDate'
                            value={state.values.startDate}
                            onChange={value => state.update('startDate', value)}
                            disablePast={true}
                        />
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <DateField
                            label={translate('endDate')}
                            format='DD/MM/YYYY'
                            name='endDate'
                            value={state.values.endDate}
                            onChange={value => state.update('endDate', value)}
                            disablePast={true}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <SelectField
                            label={translate('blockingType')}
                            name='typeId'
                            value={state.values.typeId}
                            onChange={e => state.update(e.target.name, e.target.value)}
                            data={(enterprise?.partnerPms.roomBlockingTypes ?? []).map(r => {
                                return {
                                    value: +r.id,
                                    text: r.description
                                };
                            })}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField 
                            label={translate('description')} 
                            name='description' 
                            value={state.values.description} 
                            onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 100))}
                            multiline={true}
                            minRows={6}
                            maxRows={6}
                        />
                    </Grid>
                </Grid>

                <ConfirmationDialog 
                    open={confirmationDialog.open}
                    handleClose={() => confirmationDialog.handleClose()}
                />
            </Dialog>
        </>
    );
}

export default AddRoomBlockingDialog;




const ConfirmationDialog: React.FunctionComponent<IDefaultDialogProps> = ({
    open,
    handleClose
}) => {
    const { translate } = useLanguageContext();

    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='no' onClick={handleClose} />,
            <ActionButton action='yes' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate('attention')}
            dialogActions={dialogActions}
            className='dialogactions-spacebetween overflow-inherit'
            absolute={true}
            dividers={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item xs={12}>
                    <Typography variant='body1'>
                        {translate('_roomBlockingDialogAddConfirmationDescription')}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant='body1'>
                        {translate('_proceedActionAskDescription')}
                    </Typography>
                </Grid>
            </Grid>
        </Dialog>
    );
}